import { useState, useEffect } from "react";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import Logo from "../components/Logo";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { 
  Users, 
  DollarSign, 
  Copy, 
  CheckCircle2, 
  Clock, 
  ArrowRight,
  Loader2,
  ExternalLink,
  Gift,
  TrendingUp,
  Percent,
  Banknote,
  Share2,
  Settings,
  X,
  Lightbulb,
  Shield,
  AlertTriangle,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Target,
  Mail,
  MessageSquare,
  Image,
  Building
} from "lucide-react";

const AffiliatesPage = ({ isEmbedded = false }) => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [affiliateData, setAffiliateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPlaybook, setShowPlaybook] = useState(false);
  const [applying, setApplying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedTemplate, setCopiedTemplate] = useState(null);
  const [activeTemplate, setActiveTemplate] = useState("email");
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Settings modal state
  const [showSettings, setShowSettings] = useState(false);
  const [settingsData, setSettingsData] = useState({
    payout_email: "",
    notify_new_referral: true,
    notify_payout_sent: true
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  
  // Application form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    payout_email: ""
  });
  const [needsLogin, setNeedsLogin] = useState(false);

  // Check affiliate status on mount OR redirect active affiliates
  useEffect(() => {
    let isMounted = true;
    
    const initPage = async () => {
      const params = new URLSearchParams(window.location.search);
      
      if (params.get("demo") === "true") {
        setIsDemo(true);
        fetchDemoData();
        return;
      }
      
      // Pre-fill form with user data if logged in
      if (user) {
        setFormData(prev => ({
          ...prev,
          name: user.name || "",
          email: user.email || ""
        }));
      }
      
      // Check affiliate status (unless embedded in dashboard)
      if (!isEmbedded) {
        try {
          const res = await api.get("/affiliates/me", {
            headers: { 'Cache-Control': 'no-cache' }
          });
          
          console.log("AffiliatesPage mount - /affiliates/me response:", res.data);
          
          if (!isMounted) return;
          
          // Active affiliate → redirect to dashboard
          if (res.data?.is_affiliate === true && res.data?.status === "active") {
            console.log("Active affiliate - redirecting to dashboard");
            navigate("/affiliates/dashboard", { replace: true });
            return;
          }
          
          setAffiliateData(res.data);
          if (res.data) {
            setSettingsData({
              payout_email: res.data.payout_email || res.data.email || "",
              notify_new_referral: res.data.notify_new_referral ?? true,
              notify_payout_sent: res.data.notify_payout_sent ?? true
            });
          }
        } catch (error) {
          // Handle 401 - needs login
          if (error.response?.status === 401 || error.response?.data?.needs_login) {
            console.log("Not authenticated - showing apply form");
            setNeedsLogin(false); // They can still apply without login
          } else {
            console.error("Failed to fetch affiliate data:", error);
          }
        } finally {
          if (isMounted) setLoading(false);
        }
      } else {
        // Embedded mode - just fetch data
        fetchAffiliateData();
      }
    };
    
    initPage();
    
    return () => {
      isMounted = false;
    };
  }, [user, isEmbedded, navigate]);

  const fetchDemoData = async () => {
    try {
      const res = await api.get("/admin/demo-affiliate");
      setAffiliateData(res.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to fetch demo data:", error);
      toast.error("Demo mode requires admin access");
      setIsDemo(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchAffiliateData = async () => {
    try {
      const res = await api.get("/affiliates/me", {
        headers: { 'Cache-Control': 'no-cache' }
      });
      console.log("fetchAffiliateData - /affiliates/me response:", res.data);
      
      setAffiliateData(res.data);
      setLastUpdated(new Date());
      // Initialize settings from affiliate data
      if (res.data) {
        setSettingsData({
          payout_email: res.data.payout_email || res.data.email || "",
          notify_new_referral: res.data.notify_new_referral ?? true,
          notify_payout_sent: res.data.notify_payout_sent ?? true
        });
      }
    } catch (error) {
      console.error("Failed to fetch affiliate data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    setApplying(true);
    
    try {
      const res = await api.post("/affiliates/apply", formData);
      console.log("Apply response:", res.data);
      
      // Check if needs login (pending_verification)
      if (res.data?.needs_login || res.data?.status === "pending_verification") {
        setNeedsLogin(true);
        toast.success("Application received! Sign in with Google to activate.");
        navigate("/affiliates/verify");
        return;
      }
      
      // Status is "active" - user is fully approved
      if (res.data?.status === "active") {
        toast.success("You're approved! Welcome to the affiliate program.");
        navigate("/affiliates/dashboard", { replace: true });
        return;
      }
      
      // Fallback - go to verify page
      navigate("/affiliates/verify");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Failed to submit application");
      setApplying(false);
    }
  };

  const copyReferralLink = () => {
    if (affiliateData?.referral_link) {
      navigator.clipboard.writeText(affiliateData.referral_link);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareReferralLink = async () => {
    if (affiliateData?.referral_link && navigator.share) {
      try {
        await navigator.share({
          title: "ReplyDriven - Weekly Profit Insights for Shopify",
          text: "Check out ReplyDriven - it finds profit leaks in your Shopify store every week.",
          url: affiliateData.referral_link
        });
      } catch (err) {
        // User cancelled or share failed - fallback to copy
        copyReferralLink();
      }
    } else {
      copyReferralLink();
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatTimeAgo = (date) => {
    if (!date) return "";
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffSecs < 60) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  // Promo Templates
  const getPromoTemplates = (referralLink) => ({
    email: {
      title: "Email - Agencies / Consultants",
      content: `I've started using a simple tool called ReplyDriven to monitor Shopify stores week-over-week.

It sends one email every Monday showing the single biggest revenue leak - plus the exact action to fix it.

I'm recommending it to clients who want clearer priorities without digging through analytics.

Example + free trial here:
${referralLink}`
    },
    shortEmail: {
      title: "Short Email (Forward-Friendly)",
      content: `Sharing a tool I've been testing for Shopify stores.

ReplyDriven sends one weekly email showing the biggest revenue leak - and exactly what to fix.

No dashboards, no noise.

Free trial here:
${referralLink}`
    },
    dm: {
      title: "DM (Twitter / LinkedIn / Slack)",
      content: `I've been using a Shopify tool that emails you the single biggest revenue issue each week - with the fix.

No dashboards. Just one clear action every Monday.

Free to try here:
${referralLink}`
    },
    community: {
      title: "Community Post",
      content: `I've been testing ReplyDriven on a Shopify store.

It sends one weekly email showing the biggest revenue leak - and exactly what to fix.

Free trial + example report here:
${referralLink}`
    },
    screenshot: {
      title: "Screenshot Post",
      content: `This is the weekly email I get for Shopify stores now.

One issue. One fix.

Much easier than checking analytics all week.

${referralLink}`
    }
  });

  const copyTemplate = (templateKey) => {
    const templates = getPromoTemplates(affiliateData?.referral_link || "");
    const template = templates[templateKey];
    if (template) {
      navigator.clipboard.writeText(template.content);
      setCopiedTemplate(templateKey);
      toast.success("Template copied!");
      setTimeout(() => setCopiedTemplate(null), 2000);
    }
  };

  const handleRequestPayout = async () => {
    setRequestingPayout(true);
    try {
      const res = await api.post("/affiliates/request-payout");
      toast.success(res.data.message || "Payout request submitted!");
      fetchAffiliateData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request payout");
    } finally {
      setRequestingPayout(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/affiliates/settings", settingsData);
      toast.success("Settings saved!");
      setShowSettings(false);
      fetchAffiliateData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  // Settings Modal
  const renderSettingsModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={() => setShowSettings(false)}
      />
      
      {/* Modal */}
      <div className="relative bg-[#0a0a0a] border border-[#262626] rounded-xl w-full max-w-md p-6 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl font-bold text-white">Affiliate settings</h2>
          <button 
            onClick={() => setShowSettings(false)}
            className="text-text-muted hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* PayPal Email */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-white mb-1.5">PayPal email (for payouts)</label>
          <Input
            type="email"
            value={settingsData.payout_email}
            onChange={(e) => setSettingsData({ ...settingsData, payout_email: e.target.value })}
            placeholder="your@paypal.email"
            className="bg-[#1a1a1a] border-[#262626] text-white"
            data-testid="settings-payout-email"
          />
        </div>
        
        {/* Email Notifications */}
        <div className="mb-6 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-muted">Email me when I get a new referral</span>
            <Switch
              checked={settingsData.notify_new_referral}
              onCheckedChange={(checked) => setSettingsData({ ...settingsData, notify_new_referral: checked })}
              data-testid="settings-notify-referral"
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-muted">Email me when a payout is sent</span>
            <Switch
              checked={settingsData.notify_payout_sent}
              onCheckedChange={(checked) => setSettingsData({ ...settingsData, notify_payout_sent: checked })}
              data-testid="settings-notify-payout"
            />
          </div>
        </div>
        
        {/* Save Button */}
        <Button
          onClick={handleSaveSettings}
          disabled={savingSettings}
          className="w-full bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold"
          data-testid="settings-save-btn"
        >
          {savingSettings ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            "Save settings"
          )}
        </Button>
      </div>
    </div>
  );

  // Render affiliate dashboard for approved affiliates
  const renderAffiliateDashboard = () => {
    const stats = affiliateData?.stats || {};
    const payout = affiliateData?.payout || {};
    const hasReferrals = (stats.referred_users || 0) > 0;
    const amountNeeded = Math.max(0, (payout.threshold || 50) - (stats.available_for_payout || 0));
    
    return (
      <div className="space-y-6">
        {/* Demo Mode Banner */}
        {isDemo && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-3 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
            <div>
              <p className="text-yellow-400 font-medium text-sm">Demo Mode</p>
              <p className="text-xs text-text-muted">Viewing demo affiliate dashboard. Data is not real.</p>
            </div>
          </div>
        )}

        {/* Header with Settings */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white">Affiliate Dashboard</h1>
            {/* Post-signup recovery helper */}
            <p className="text-xs text-text-muted mt-1">You can always access this page from the top-right menu.</p>
          </div>
          {!isDemo && (
            <button
              onClick={() => setShowSettings(true)}
              className="flex items-center gap-1.5 text-text-muted hover:text-white transition-colors text-sm"
              data-testid="settings-btn"
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
          )}
        </div>

        {/* Server-Side Attribution Info Box */}
        <Card className="bg-gradient-to-r from-[#0d1a14] to-[#0a1510] border-[#1a3025]" data-testid="attribution-info-box">
          <CardContent className="py-5">
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-[#D4F34A]/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-[#D4F34A]" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Your referrals are tracked automatically</h3>
                <p className="text-sm text-text-muted leading-relaxed">
                  When someone signs up through your link, we permanently attach them to your affiliate account.
                  Even if billing isn&apos;t open yet (waitlist mode), you&apos;ll still get credit when they subscribe later - they do NOT need to click your link again.
                </p>
                <p className="text-xs text-text-muted mt-2 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-[#D4F34A]" />
                  Tracking is server-side and survives cookie clears.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Referral Link Card */}
        <Card className="bg-surface border-[#262626] shadow-[0_0_30px_rgba(212,243,74,0.08)]">
          <CardContent className="py-6">
            <div className="mb-4">
              <h3 className="font-heading text-lg font-bold text-white mb-1">Your referral link</h3>
              <p className="text-text-muted text-sm">Share this link to earn 30% recurring commission for every customer who subscribes through it.</p>
              <p className="text-xs text-text-muted mt-1">For agencies, we recommend using the <a href={`https://replydriven.com/agency?ref=${affiliateData?.code || ""}`} target="_blank" rel="noopener noreferrer" className="text-[#D4F34A] hover:underline">Agency Page</a>.</p>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-[#0a0a0a] border border-[#262626] rounded-lg px-4 py-2.5 font-mono text-sm text-white truncate">
                {affiliateData?.referral_link}
              </div>
              <Button
                onClick={copyReferralLink}
                variant="outline"
                className="border-[#262626] text-white hover:bg-white/5 px-3"
                data-testid="copy-referral-link"
              >
                {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span className="ml-2 hidden sm:inline">{copied ? "Copied" : "Copy link"}</span>
              </Button>
              <Button
                onClick={shareReferralLink}
                className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold px-3"
                data-testid="share-referral-link"
              >
                <Share2 className="w-4 h-4" />
                <span className="ml-2 hidden sm:inline">Share</span>
              </Button>
            </div>
            
            <p className="text-xs text-[#D4F34A]/80 mt-3 flex items-center gap-1.5">
              <TrendingUp className="w-3 h-3" />
              Send traffic now - waitlist signups are credited automatically.
            </p>
          </CardContent>
        </Card>

        {/* Promo Templates Card */}
        <Card className="bg-surface border-[#262626]">
          <CardContent className="py-6">
            <div className="mb-4">
              <h3 className="font-heading text-lg font-bold text-white mb-1">Promo Templates (Copy & Share)</h3>
              <p className="text-text-muted text-sm">Ready-to-use messages with your referral link included.</p>
            </div>

            {/* Template Tabs */}
            <div className="flex flex-wrap gap-2 mb-4 border-b border-[#262626] pb-4">
              {[
                { key: "email", label: "Email" },
                { key: "shortEmail", label: "Short Email" },
                { key: "dm", label: "DM" },
                { key: "community", label: "Community" },
                { key: "screenshot", label: "Screenshot" }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTemplate(tab.key)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    activeTemplate === tab.key 
                      ? "bg-[#D4F34A] text-black font-medium" 
                      : "bg-[#1a1a1a] text-text-muted hover:bg-[#262626] hover:text-white"
                  }`}
                  data-testid={`template-tab-${tab.key}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Active Template Content */}
            {(() => {
              const templates = getPromoTemplates(affiliateData?.referral_link || "");
              const template = templates[activeTemplate];
              return (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-white">{template?.title}</span>
                    <Button
                      onClick={() => copyTemplate(activeTemplate)}
                      size="sm"
                      className={copiedTemplate === activeTemplate 
                        ? "bg-green-500/20 text-green-400 hover:bg-green-500/30" 
                        : "bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90"}
                      data-testid="copy-template-btn"
                    >
                      {copiedTemplate === activeTemplate ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="bg-[#0a0a0a] border border-[#262626] rounded-lg p-4">
                    <pre className="text-sm text-text-muted whitespace-pre-wrap font-sans leading-relaxed">
                      {template?.content}
                    </pre>
                  </div>
                </div>
              );
            })()}

            {/* Tip */}
            <p className="text-xs text-text-muted mt-4 flex items-start gap-2">
              <Lightbulb className="w-3 h-3 mt-0.5 text-[#D4F34A] flex-shrink-0" />
              Tip: These work best when you add one personal sentence at the top.
            </p>
          </CardContent>
        </Card>

        {/* Agency Landing Page CTA */}
        <Card className="bg-gradient-to-r from-[#D4F34A]/5 to-transparent border-[#D4F34A]/20">
          <CardContent className="py-5">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center flex-shrink-0">
                  <Building className="w-5 h-5 text-[#D4F34A]" />
                </div>
                <div>
                  <h3 className="font-heading text-base font-bold text-white">Agency Landing Page</h3>
                  <p className="text-sm text-text-muted">Use this page when sharing ReplyDriven with clients or other agencies.</p>
                </div>
              </div>
              <a 
                href={`https://replydriven.com/agency?ref=${affiliateData?.code || ""}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0"
                data-testid="agency-page-cta"
              >
                <Button className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold">
                  Open Agency Page
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Mini Affiliate Playbook - Collapsible */}
        <Card className="bg-surface border-[#262626]">
          <CardContent className="py-4">
            <button
              onClick={() => setShowPlaybook(!showPlaybook)}
              className="w-full flex items-center justify-between"
              data-testid="playbook-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-[#D4F34A]" />
                </div>
                <div className="text-left">
                  <h3 className="font-heading text-base font-bold text-white">Affiliate Playbook (3-Minute Read)</h3>
                  <p className="text-xs text-text-muted">How to earn recurring revenue with ReplyDriven</p>
                </div>
              </div>
              {showPlaybook ? (
                <ChevronUp className="w-5 h-5 text-text-muted" />
              ) : (
                <ChevronDown className="w-5 h-5 text-text-muted" />
              )}
            </button>

            {showPlaybook && (
              <div className="mt-6 space-y-6 border-t border-[#262626] pt-6">
                {/* Section 1: What You're Promoting */}
                <div>
                  <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <span className="text-[#D4F34A]">1.</span> What You&apos;re Promoting
                  </h4>
                  <p className="text-sm text-text-muted mb-3">
                    ReplyDriven is <span className="text-white">NOT</span> another Shopify analytics tool. It:
                  </p>
                  <ul className="text-sm text-text-muted space-y-1 mb-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-3 h-3 mt-1 text-[#D4F34A] flex-shrink-0" />
                      Monitors a store weekly
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-3 h-3 mt-1 text-[#D4F34A] flex-shrink-0" />
                      Finds the single most expensive issue
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-3 h-3 mt-1 text-[#D4F34A] flex-shrink-0" />
                      Emails one clear action to fix it
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-3 h-3 mt-1 text-[#D4F34A] flex-shrink-0" />
                      No dashboards. No noise.
                    </li>
                  </ul>
                  <div className="bg-[#0a0a0a] border border-[#262626] rounded-lg p-3">
                    <p className="text-sm text-white italic">&quot;My weekly profit monitoring system for Shopify stores.&quot;</p>
                  </div>
                </div>

                {/* Section 2: Who Converts Best */}
                <div>
                  <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <span className="text-[#D4F34A]">2.</span> Who Converts Best
                  </h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-[#D4F34A] font-medium mb-2">✅ PROMOTE TO</p>
                      <ul className="text-sm text-text-muted space-y-1">
                        <li>• Shopify stores doing $20k+/month</li>
                        <li>• Busy founders / operators</li>
                        <li>• Agencies & consultants</li>
                      </ul>
                    </div>
                    <div>
                      <p className="text-xs text-red-400 font-medium mb-2">❌ AVOID</p>
                      <ul className="text-sm text-text-muted space-y-1">
                        <li>• New stores</li>
                        <li>• Dropshipping experiments</li>
                        <li>• &quot;More data&quot; people</li>
                      </ul>
                    </div>
                  </div>
                  <p className="text-xs text-text-muted mt-2">Better fit = higher retention = more recurring commission.</p>
                </div>

                {/* Section 3: How to Promote */}
                <div>
                  <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <span className="text-[#D4F34A]">3.</span> How to Promote (Pick ONE)
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-start gap-3 bg-[#0a0a0a] border border-[#262626] rounded-lg p-3">
                      <Mail className="w-4 h-4 text-[#D4F34A] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-white">Option A - Email (Highest ROI)</p>
                        <p className="text-xs text-text-muted">Send to clients, past clients, internal lists. Use templates above.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 bg-[#0a0a0a] border border-[#262626] rounded-lg p-3">
                      <MessageSquare className="w-4 h-4 text-[#D4F34A] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-white">Option B - DM (Low Effort)</p>
                        <p className="text-xs text-text-muted">Twitter / LinkedIn / Slack. Best with operators you know.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 bg-[#0a0a0a] border border-[#262626] rounded-lg p-3">
                      <Image className="w-4 h-4 text-[#D4F34A] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-white">Option C - Screenshot Post (High Trust)</p>
                        <p className="text-xs text-text-muted">Screenshot the sample report. Very high conversion.</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-text-muted mt-2">You don&apos;t need to do all three. One is enough.</p>
                </div>

                {/* Section 4: What NOT to Say */}
                <div>
                  <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <span className="text-[#D4F34A]">4.</span> What NOT to Say
                  </h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3">
                      <p className="text-xs text-red-400 font-medium mb-2">❌ DON&apos;T SAY</p>
                      <ul className="text-sm text-text-muted space-y-1">
                        <li>• Promise revenue</li>
                        <li>• Say &quot;guaranteed&quot;</li>
                        <li>• Hype AI</li>
                        <li>• Oversell</li>
                      </ul>
                    </div>
                    <div className="bg-[#D4F34A]/5 border border-[#D4F34A]/20 rounded-lg p-3">
                      <p className="text-xs text-[#D4F34A] font-medium mb-2">✅ DO SAY</p>
                      <ul className="text-sm text-text-muted space-y-1">
                        <li>• &quot;monitoring&quot;</li>
                        <li>• &quot;weekly clarity&quot;</li>
                        <li>• &quot;one clear action&quot;</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Section 5: How You Get Paid */}
                <div>
                  <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <span className="text-[#D4F34A]">5.</span> How You Get Paid
                  </h4>
                  <ul className="text-sm text-text-muted space-y-1">
                    <li className="flex items-start gap-2">
                      <DollarSign className="w-3 h-3 mt-1 text-[#D4F34A] flex-shrink-0" />
                      30% recurring commission
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-3 h-3 mt-1 text-[#D4F34A] flex-shrink-0" />
                      Earned on monthly + yearly plans
                    </li>
                    <li className="flex items-start gap-2">
                      <Banknote className="w-3 h-3 mt-1 text-[#D4F34A] flex-shrink-0" />
                      Payouts via PayPal (min $50)
                    </li>
                  </ul>
                  <p className="text-xs text-text-muted mt-2">Once someone subscribes, you earn every billing cycle.</p>
                </div>

                {/* Section 6: Pro Tip */}
                <div className="bg-[#D4F34A]/5 border border-[#D4F34A]/20 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-[#D4F34A]" />
                    Pro Tip (Increases Conversions)
                  </h4>
                  <p className="text-sm text-text-muted mb-2">Add one personal line before the template text:</p>
                  <p className="text-sm text-white italic">&quot;We&apos;ve started using this internally for client stores.&quot;</p>
                  <p className="text-xs text-text-muted mt-2">That single sentence doubles trust.</p>
                </div>

                {/* Section 7: Your Assets */}
                <div>
                  <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <span className="text-[#D4F34A]">7.</span> Your Assets
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-[#0a0a0a] border border-[#262626] rounded-lg p-3 text-center">
                      <p className="text-sm text-white">Referral Link</p>
                      <p className="text-xs text-text-muted">Auto-tracked</p>
                    </div>
                    <div className="bg-[#0a0a0a] border border-[#262626] rounded-lg p-3 text-center">
                      <p className="text-sm text-white">Promo Templates</p>
                      <p className="text-xs text-text-muted">Copy & paste</p>
                    </div>
                    <div className="bg-[#0a0a0a] border border-[#262626] rounded-lg p-3 text-center">
                      <p className="text-sm text-white">Sample Report</p>
                      <p className="text-xs text-text-muted">Screenshot-friendly</p>
                    </div>
                    <a 
                      href={`https://replydriven.com/agency?ref=${affiliateData?.code || ""}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#0a0a0a] border border-[#262626] rounded-lg p-3 text-center hover:border-[#D4F34A]/50 transition-colors"
                    >
                      <p className="text-sm text-white">Agency Page</p>
                      <p className="text-xs text-[#D4F34A]">Share with agencies →</p>
                    </a>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats Grid with Last Updated */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-text-muted">Stats</span>
          {lastUpdated && (
            <span className="text-xs text-text-muted">Last updated: {formatTimeAgo(lastUpdated)}</span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-surface border-[#262626]">
            <CardContent className="py-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#D4F34A]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.referred_users || 0}</p>
                  <p className="text-xs text-text-muted">Referred users</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-surface border-[#262626]">
            <CardContent className="py-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{formatCurrency(stats.total_earned || 0)}</p>
                  <p className="text-xs text-text-muted">Total earned</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-surface border-[#262626]">
            <CardContent className="py-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{formatCurrency(stats.pending_amount || 0)}</p>
                  <p className="text-xs text-text-muted">Pending payout</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-surface border-[#262626]">
            <CardContent className="py-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{formatCurrency(stats.paid_amount || 0)}</p>
                  <p className="text-xs text-text-muted">Paid</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Empty State - Next Steps (show when no referrals) */}
        {!hasReferrals && (
          <Card className="bg-surface border-[#262626]">
            <CardContent className="py-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="w-5 h-5 text-[#D4F34A]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-lg font-bold text-white mb-2">Get your first referral</h3>
                  <p className="text-sm text-text-muted mb-4">
                    Use the promo templates above to share in Shopify / ecom groups, or share a screenshot of the sample report.
                  </p>
                  <button 
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="inline-flex items-center gap-2 bg-[#D4F34A] text-black font-bold text-sm px-4 py-2 rounded-lg hover:bg-[#D4F34A]/90 transition-colors"
                  >
                    View promo templates
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payout Request Section */}
        <Card className="bg-surface border-[#262626]">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Banknote className="w-5 h-5 text-[#D4F34A]" />
                  <h3 className="font-heading text-lg font-bold text-white">Available for payout</h3>
                </div>
                <p className="text-3xl font-bold text-[#D4F34A] mb-2">{formatCurrency(stats.available_for_payout || 0)}</p>
                {payout.can_request ? (
                  <p className="text-sm text-green-400">You can request a payout now.</p>
                ) : amountNeeded > 0 ? (
                  <p className="text-sm text-text-muted">Earn {formatCurrency(amountNeeded)} more to request a payout.</p>
                ) : null}
              </div>
              <div className="flex flex-col items-start md:items-end gap-2">
                {payout.requested ? (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-3">
                    <p className="text-yellow-400 font-medium flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Payout Requested
                    </p>
                    <p className="text-xs text-text-muted mt-1">
                      {payout.requested_at ? new Date(payout.requested_at).toLocaleDateString() : "Processing..."}
                    </p>
                  </div>
                ) : (
                  <Button
                    onClick={handleRequestPayout}
                    disabled={!payout.can_request || requestingPayout}
                    className={payout.can_request 
                      ? "bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold" 
                      : "bg-[#262626] text-gray-500 cursor-not-allowed"}
                    data-testid="request-payout-btn"
                  >
                    {requestingPayout ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Banknote className="w-4 h-4 mr-2" />
                    )}
                    Request payout
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Commissions */}
        {affiliateData?.recent_commissions?.length > 0 && (
          <Card className="bg-surface border-[#262626]">
            <CardHeader>
              <CardTitle className="text-white">Recent Commissions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {affiliateData.recent_commissions.map((commission) => (
                  <div 
                    key={commission.commission_id}
                    className="flex items-center justify-between py-3 border-b border-[#262626] last:border-0"
                  >
                    <div>
                      <p className="font-medium text-white">{commission.plan} Plan</p>
                      <p className="text-sm text-text-muted">
                        {new Date(commission.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#D4F34A]">{formatCurrency(commission.commission_amount)}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        commission.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                        commission.status === 'approved' ? 'bg-blue-500/20 text-blue-400' :
                        commission.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {commission.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trust Footer Note */}
        <div className="flex items-center justify-center gap-2 text-xs text-text-muted py-4">
          <Shield className="w-3 h-3" />
          <span>We never modify customer data. Referral tracking is first-party and privacy-safe.</span>
        </div>
      </div>
    );
  };

  // Render pending state
  const renderPendingState = () => (
    <Card className="bg-surface border-[#262626] max-w-lg mx-auto">
      <CardContent className="py-8 text-center">
        <div className="w-16 h-16 rounded-full bg-[#D4F34A]/10 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-[#D4F34A]" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-white mb-2">You&apos;re Approved!</h2>
        <p className="text-text-muted mb-6">
          Check your email for your referral link and start earning 30% commissions today.
        </p>
        <Link to="/">
          <Button variant="outline" className="border-[#262626] text-white hover:bg-white/5">
            Back to Home
          </Button>
        </Link>
      </CardContent>
    </Card>
  );

  // Render application form
  const renderApplicationForm = () => (
    <div className="max-w-2xl mx-auto">
      {/* Hero */}
      <div className="text-center mb-12">
        <div className="w-16 h-16 rounded-full bg-[#D4F34A]/10 flex items-center justify-center mx-auto mb-6">
          <Gift className="w-8 h-8 text-[#D4F34A]" />
        </div>
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
          Earn <span className="text-[#D4F34A]">30%</span> Recurring Commissions
        </h1>
        <p className="text-text-muted text-lg max-w-xl mx-auto">
          Join the ReplyDriven affiliate program and earn recurring revenue for every customer you refer.
        </p>
      </div>

      {/* Benefits */}
      <div className="grid md:grid-cols-3 gap-4 mb-12">
        <div className="bg-surface border border-[#262626] rounded-xl p-5 text-center">
          <Percent className="w-8 h-8 text-[#D4F34A] mx-auto mb-3" />
          <h3 className="font-semibold text-white mb-1">30% Commission</h3>
          <p className="text-sm text-text-muted">On every payment, every month</p>
        </div>
        <div className="bg-surface border border-[#262626] rounded-xl p-5 text-center">
          <TrendingUp className="w-8 h-8 text-[#D4F34A] mx-auto mb-3" />
          <h3 className="font-semibold text-white mb-1">Recurring Revenue</h3>
          <p className="text-sm text-text-muted">Earn as long as they&apos;re subscribed</p>
        </div>
        <div className="bg-surface border border-[#262626] rounded-xl p-5 text-center">
          <Clock className="w-8 h-8 text-[#D4F34A] mx-auto mb-3" />
          <h3 className="font-semibold text-white mb-1">30-Day Cookie</h3>
          <p className="text-sm text-text-muted">Get credit for delayed signups</p>
        </div>
      </div>

      {/* Application Form */}
      <Card className="bg-surface border-[#262626]">
        <CardHeader>
          <CardTitle className="text-white">Apply to Become an Affiliate</CardTitle>
          <CardDescription className="text-text-muted">
            Fill out the form below. Get instant approval and start earning today.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleApply} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1.5">Full Name</label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your name"
                required
                className="bg-[#1a1a1a] border-[#262626] text-white"
                data-testid="affiliate-name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-1.5">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                required
                className="bg-[#1a1a1a] border-[#262626] text-white"
                data-testid="affiliate-email"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-1.5">
                PayPal Email <span className="text-text-muted">(for payouts)</span>
              </label>
              <Input
                type="email"
                value={formData.payout_email}
                onChange={(e) => setFormData({ ...formData, payout_email: e.target.value })}
                placeholder="Same as above if blank"
                className="bg-[#1a1a1a] border-[#262626] text-white"
                data-testid="affiliate-payout-email"
              />
            </div>

            <Button
              type="submit"
              disabled={applying}
              className="w-full bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold py-6 text-lg"
              data-testid="affiliate-submit"
            >
              {applying ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  Apply Now <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Sign in prompt for non-logged-in users */}
      {!user && (
        <p className="text-center text-text-muted text-sm mt-6">
          Already an affiliate?{" "}
          <button onClick={login} className="text-[#D4F34A] hover:underline">
            Sign in
          </button>{" "}
          to view your dashboard.
        </p>
      )}
    </div>
  );

  // When embedded (in AffiliateDashboard), only return the content without nav/footer
  if (isEmbedded) {
    return (
      <div className="text-white">
        {/* Settings Modal */}
        {showSettings && renderSettingsModal()}

        <main className="py-8 px-6 md:px-12">
          <div className="max-w-5xl mx-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin" />
              </div>
            ) : affiliateData?.is_affiliate && affiliateData?.status === "active" ? (
              renderAffiliateDashboard()
            ) : affiliateData?.status === "pending" ? (
              renderPendingState()
            ) : (
              renderApplicationForm()
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Settings Modal */}
      {showSettings && renderSettingsModal()}

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Logo showText linkTo="/" />
          
          <div className="flex items-center gap-4">
            {/* Show Affiliate Dashboard link if user is active affiliate */}
            {user && affiliateData?.is_affiliate && affiliateData?.status === "active" && (
              <Link to="/affiliates/dashboard">
                <Button 
                  variant="outline" 
                  className="border-[#D4F34A] text-[#D4F34A] hover:bg-[#D4F34A]/10 font-bold rounded-full px-6 py-2"
                  data-testid="go-to-affiliate-dashboard"
                >
                  Go to Dashboard
                </Button>
              </Link>
            )}
            {user ? (
              <Link to="/dashboard">
                <Button className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold rounded-full px-6 py-2">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Button
                onClick={login}
                className="bg-white/10 text-white hover:bg-white/20 border border-white/10 rounded-full px-6 py-2"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </nav>

      <main className="pt-28 pb-16 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin" />
            </div>
          ) : affiliateData?.is_affiliate && affiliateData?.status === "active" ? (
            renderAffiliateDashboard()
          ) : affiliateData?.status === "pending" ? (
            renderPendingState()
          ) : (
            renderApplicationForm()
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#262626]">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <p className="text-text-muted text-sm">© {new Date().getFullYear()} ReplyDriven</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-text-muted hover:text-white text-sm">Privacy</Link>
            <Link to="/terms" className="text-text-muted hover:text-white text-sm">Terms</Link>
            <a href="mailto:hello@replydriven.com" className="text-text-muted hover:text-white text-sm">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AffiliatesPage;
